<script lang="ts">
	import type { Numeric, ScaleContinuousNumeric } from 'd3';
	import type { Snippet } from 'svelte';

	interface Props {
		/**
		 * Which side of the axis you want the ticks to appear on.
		 *
		 * This includes left, right, and both, along with complements using CSS logical properties
		 */
		position: 'left' | 'right' | 'inline-start' | 'inline-end' | 'inline' | 'both';
		/**
		 * The actual ticks
		 */
		ticks: Numeric[];
		/**
		 * The scale for the axis
		 */
		scale: ScaleContinuousNumeric<Numeric, Numeric>;
		/**
		 * The units for the positioning. Defaults to pixels if unspecified
		 */
		units?: 'px' | '%';
		/**
		 * A snippet for rendering the tick
		 */
		tick?: Snippet<[Numeric, number, Numeric[]]>;
		/**
		 * A snippet for rendering the label
		 */
		label?: Snippet<[Numeric, number, Numeric[]]>;
		/**
		 * A snippet for rendering the line
		 */
		line?: Snippet<[Numeric, number, Numeric[]]>;
	}

	let { position, ticks, scale, units = 'px', tick, label, line }: Props = $props();
</script>

{#snippet renderTicks(pos: 'left' | 'right' | 'inline-start' | 'inline-end')}
	{#each ticks as t, idx (t)}
		{#if tick}
			<div
				class={[
					'g-tick',
					pos === 'left' && 'g-left',
					pos === 'right' && 'g-right',
					pos === 'inline-start' && 'g-start',
					pos === 'inline-end' && 'g-end',
				]}
				style:top="{scale(t)}{units}"
			>
				{@render tick(t, idx, ticks)}
			</div>
		{/if}
		{#if label}
			<div
				class={[
					'g-label',
					pos === 'left' && 'g-left',
					pos === 'right' && 'g-right',
					pos === 'inline-start' && 'g-start',
					pos === 'inline-end' && 'g-end',
				]}
				style:top="{scale(t)}{units}"
			>
				{@render label(t, idx, ticks)}
			</div>
		{/if}
	{/each}
{/snippet}

<div class="g-axis">
	{#if position === 'both'}
		{@render renderTicks('left')}
		{@render renderTicks('right')}
	{:else if position === 'inline'}
		{@render renderTicks('inline-start')}
		{@render renderTicks('inline-end')}
	{:else}
		{@render renderTicks(position)}
	{/if}
	{#if line}
		{#each ticks as t, idx (t)}
			<div class="g-line" style:top="{scale(t)}{units}">
				{@render line(t, idx, ticks)}
			</div>
		{/each}
	{/if}
</div>

<style>
	.g-tick,
	.g-label,
	.g-line {
		position: absolute;
	}

	.g-tick.g-left,
	.g-label.g-left,
	.g-line {
		left: 0;
	}

	.g-tick.g-right,
	.g-label.g-right {
		right: 0;
	}

	.g-tick.g-start,
	.g-label.g-start {
		inset-inline-start: 0;
	}

	.g-tick.g-end,
	.g-label.g-end {
		inset-inline-end: 0;
	}

	.g-label.g-left,
	.g-label.g-start:dir(ltr),
	.g-label.g-end:dir(rtl) {
		transform: translate(-100%, -50%);
	}

	.g-label.g-right,
	.g-label.g-end:dir(ltr),
	.g-label.g-start:dir(rtl) {
		transform: translate(100%, -50%);
	}

	.g-line {
		width: 100%;
	}

	.g-label {
		line-height: 1;
	}
</style>
